import { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import {
  Combobox,
  Dropdown,
  Option,
  Button,
  Badge,
  Text,
  Spinner,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { isSpaceBookable } from '@/utils/spaceAvailability';
import type { SpaceWithAvailability } from '@/store/models/spaces';
import type { EventSpaceSelection } from '@/store/models/events';
import { useSelectedSpacesEditorStyles } from './SelectedSpacesEditor.styles';

const normalizeSetupCode = (value: string | undefined): string =>
  value
    ?.trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '') ?? '';

const getAvailabilityBadgeProps = (isAvailable: boolean | undefined) => {
  if (isAvailable === true)
    return { color: 'success' as const, label: 'reviewPanel.availabilityBadge.available' };
  if (isAvailable === false)
    return { color: 'danger' as const, label: 'reviewPanel.availabilityBadge.booked' };
  return { color: 'warning' as const, label: 'reviewPanel.availabilityBadge.unavailable' };
};

export interface SpaceRowProps {
  selection: EventSpaceSelection;
  availableSpaces: SpaceWithAvailability[];
  onSpaceChange: (spaceId: string, spaceName: string) => void;
  onSetupChange: (codes: string[], setupCapacity: number) => void;
  onRemove: () => void;
  canRemove: boolean;
  focusOnMount?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  locationLocked?: boolean;
}

export const SpaceRow = ({
  selection,
  availableSpaces,
  onSpaceChange,
  onSetupChange,
  onRemove,
  canRemove,
  focusOnMount,
  isLoading,
  disabled = false,
  locationLocked = false,
}: SpaceRowProps) => {
  const styles = useSelectedSpacesEditorStyles();
  const { t } = useTranslation('planner');
  const comboboxRef = useRef<HTMLInputElement>(null);
  const [spaceQuery, setSpaceQuery] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (focusOnMount) comboboxRef.current?.focus();
  }, [focusOnMount]);

  const selectedSpace = useMemo(
    () => availableSpaces.find((s) => s.spaceId === selection.spaceId),
    [availableSpaces, selection.spaceId]
  );

  const setupOptions = useMemo(() => {
    const layouts = selectedSpace?.supportedRoomLayouts ?? [];
    if (layouts.length > 0) return layouts;
    // Fallback: derive from existing selection codes
    if (selection.selectedSetupCodes.length > 0) {
      return selection.selectedSetupCodes.map((code) => ({
        code: code.toUpperCase(),
        capacity: selectedSpace?.roomCapacity ?? 0,
      }));
    }
    // Default to Boardroom when space has no layout data
    if (selection.spaceId) {
      return [{ code: 'BOARDROOM', capacity: selectedSpace?.roomCapacity ?? 0 }];
    }
    return [];
  }, [selectedSpace, selection.selectedSetupCodes, selection.spaceId]);

  useEffect(() => {
    const onlySetup = setupOptions[0];
    if (
      !disabled &&
      !locationLocked &&
      selection.spaceId &&
      selection.selectedSetupCodes.length === 0 &&
      setupOptions.length === 1 &&
      onlySetup
    ) {
      onSetupChange([onlySetup.code], onlySetup.capacity);
    }
  }, [disabled, locationLocked, onSetupChange, selection.selectedSetupCodes.length, selection.spaceId, setupOptions]);

  const spaceDisplayValue = selection.spaceName || '';

  const filteredSpaces = useMemo(() => {
    if (spaceQuery === undefined || spaceQuery === '') return availableSpaces;
    const q = spaceQuery.toLowerCase();
    return availableSpaces.filter((s) => s.spaceName.toLowerCase().includes(q));
  }, [availableSpaces, spaceQuery]);

  const handleSpaceSelect = useCallback(
    (_e: unknown, data: { optionValue?: string }) => {
      if (!data.optionValue) return;
      const space = availableSpaces.find((s) => s.spaceId === data.optionValue);
      if (space) {
        onSpaceChange(space.spaceId as string, space.spaceName);
        setSpaceQuery(undefined);
      }
    },
    [availableSpaces, onSpaceChange]
  );

  const handleSetupSelect = useCallback(
    (_e: unknown, data: { selectedOptions: string[] }) => {
      const codes = data.selectedOptions;
      const capacity = setupOptions
        .filter((l) => codes.some((c) => normalizeSetupCode(c) === normalizeSetupCode(l.code)))
        .reduce((max, l) => Math.max(max, l.capacity), 0);
      onSetupChange(codes, capacity);
    },
    [setupOptions, onSetupChange]
  );

  // Map event setup codes to the canonical layout codes for Dropdown matching
  const normalizedSelectedOptions = useMemo(() => {
    return selection.selectedSetupCodes.map((code) => {
      const match = setupOptions.find(
        (l) => normalizeSetupCode(l.code) === normalizeSetupCode(code)
      );
      return match?.code ?? code;
    });
  }, [selection.selectedSetupCodes, setupOptions]);

  const roomCapacity = selectedSpace?.roomCapacity;

  const setupDisplayValue = useMemo(() => {
    if (selection.selectedSetupCodes.length === 0) return '';
    return selection.selectedSetupCodes
      .map((code) => {
        const layout = setupOptions.find(
          (l) => normalizeSetupCode(l.code) === normalizeSetupCode(code)
        );
        const meta = getRoomLayoutMeta(code);
        return layout && layout.capacity > 0 ? `${meta.title} (${layout.capacity})` : meta.title;
      })
      .join(', ');
  }, [selection.selectedSetupCodes, setupOptions]);

  const currentAvailability = getAvailabilityBadgeProps(
    selectedSpace ? isSpaceBookable(selectedSpace) : undefined
  );

  return (
    <div className={styles.spaceRow} data-testid="space-row">
      <div className={styles.spaceInputRelative}>
        <Combobox
          disabled={disabled || locationLocked}
          ref={comboboxRef}
          value={isLoading ? t('reviewPanel.loadingSpaces') : (spaceQuery ?? spaceDisplayValue)}
          selectedOptions={selection.spaceId ? [selection.spaceId as string] : []}
          onInput={(e) => setSpaceQuery((e.target as HTMLInputElement).value)}
          onFocus={(e) => {
            const input = e.target as HTMLInputElement;
            requestAnimationFrame(() => {
              input.selectionStart = input.selectionEnd = input.value.length;
              input.scrollLeft = input.scrollWidth;
            });
          }}
          onOptionSelect={handleSpaceSelect}
          onBlur={() => setSpaceQuery(undefined)}
          className={styles.comboboxFull}
          expandIcon={null}
          listbox={{ className: styles.spaceListbox }}
          placeholder="Search spaces..."
          aria-label={t('reviewPanel.selectedSpace')}
          data-testid="space-row-combobox"
        >
          {isLoading ? (
            <Option key="__loading__" value="" text="" disabled>
              <Spinner size="tiny" label={t('reviewPanel.loadingSpaces')} />
            </Option>
          ) : (
            <>
              <div className={styles.spaceOptionGroupHeader}>
                <Text className={styles.spaceOptionGroupLabel}>{t('reviewPanel.roomList')}</Text>
              </div>
              {filteredSpaces.length === 0 ? (
                <div
                  key="__no_results__"
                  role="presentation"
                  style={{ padding: '6px 12px', color: 'var(--colorNeutralForeground3)' }}
                >
                  {t('reviewPanel.noSpacesFound')}
                </div>
              ) : (
                filteredSpaces.map((s) => {
                  const badge = getAvailabilityBadgeProps(isSpaceBookable(s));
                  return (
                    <Option key={s.spaceId} value={s.spaceId} text={s.spaceName}>
                      <span className={styles.spaceOptionContent}>
                        <span>{s.spaceName}</span>
                        <Badge
                          appearance="filled"
                          color={badge.color}
                          className={styles.spaceBadgeInline}
                        >
                          {t(badge.label)}
                        </Badge>
                      </span>
                    </Option>
                  );
                })
              )}
            </>
          )}
        </Combobox>
        {selection.spaceId && !isLoading && (
          <Badge
            appearance="filled"
            color={currentAvailability.color}
            className={styles.badgeOverlay}
          >
            {t(currentAvailability.label)}
          </Badge>
        )}
      </div>

      <Dropdown
        value={isLoading ? '' : setupDisplayValue}
        selectedOptions={normalizedSelectedOptions}
        onOptionSelect={handleSetupSelect}
        disabled={disabled || locationLocked || !selection.spaceId || isLoading}
        className={styles.setupDropdown}
        aria-label={t('reviewPanel.fields.roomSetupPreference')}
        data-testid="space-row-setup"
        multiselect
        placeholder={t('reviewPanel.selectSetupPlaceholder')}
        {...(setupDisplayValue && !isLoading
          ? {
              button: {
                children: (
                  <span className={styles.setupValueText} title={setupDisplayValue}>
                    {setupDisplayValue}
                  </span>
                ),
              },
            }
          : {})}
      >
        {isLoading ? (
          <Option key="__loading__" value="" text="" disabled>
            <Spinner size="tiny" label={t('reviewPanel.loadingSpaces')} />
          </Option>
        ) : (
          setupOptions.map((layout) => {
            const meta = getRoomLayoutMeta(layout.code);
            const label = layout.capacity > 0 ? `${meta.title} (${layout.capacity})` : meta.title;
            return (
              <Option key={layout.code} value={layout.code} text={meta.title}>
                {label}
              </Option>
            );
          })
        )}
      </Dropdown>

      <Button
        icon={<Dismiss20Regular />}
        appearance="subtle"
        disabled={disabled || locationLocked || !canRemove}
        onClick={onRemove}
        className={styles.removeButton}
        aria-label={t('reviewPanel.removeSpace')}
        data-testid="space-row-remove"
      />
      {selectedSpace && typeof roomCapacity === 'number' && (
        <span className={styles.roomCapacityLabel} data-testid="space-row-capacity">
          {t('reviewPanel.roomCapacityLabel', { count: roomCapacity })}
        </span>
      )}
    </div>
  );
};

SpaceRow.displayName = 'SpaceRow';
